import React from 'react';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';

import {
  DateField,
  Form,
  FunctionField,
  RecordContextProvider,
  ReferenceField,
  TextInput,
  TextField,
  required,
  useGetIdentity,
  useNotify,
  usePermissions,
  useRefresh,
  useUpdate,
} from 'react-admin';
import { FieldValues } from 'react-hook-form';

import actions from '../../../../constants/actions';
import hasAccess from '../../../../utilities/hasAccess';
import { Permission } from '../../../../utilities/Authprovider';
import resourceNames from '../../../../constants/resourceNames';
import { PendingReview } from '../../../../hooks/usePendingReview';
import errorNotification from '../../../../utilities/errorNotification';

type RejectForm = {
  comment: string;
};

const PendingReviewBanner = ({
  review,
  onReviewResolved,
  onReviewApproved,
}: {
  review: PendingReview;
  onReviewResolved: () => void;
  onReviewApproved?: () => void;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions<Permission[]>();

  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);

  const canApprove = hasAccess(permissions, actions.RULE_VERSION_APPROVE);
  const isProposer = identity?.id !== undefined && String(identity.id) === review.proposerId;
  const canResolve = canApprove && !isProposer;

  const [approve, { isLoading: isApproving }] = useUpdate(
    'reviews',
    { id: review.id, data: { status: 'approved' } },
    {
      onSuccess: () => {
        onReviewResolved();
        onReviewApproved?.();
        refresh();
      },
      onError: (e) => {
        notify(errorNotification(e, 'Failed to approve review'), { type: 'error' });
        refresh();
      },
    },
  );

  const [withdraw, { isLoading: isWithdrawing }] = useUpdate(
    'reviews',
    { id: `${review.id}/withdraw`, data: {} },
    {
      onSuccess: () => {
        onReviewResolved();
        refresh();
      },
      onError: (e) => {
        notify(errorNotification(e, 'Failed to withdraw review'), { type: 'error' });
        refresh();
      },
    },
  );

  const [reject, { isLoading: isRejecting }] = useUpdate();

  const isLoading = isApproving || isWithdrawing || isRejecting;

  const closeRejectDialog = React.useCallback(() => {
    if (isLoading) return;
    setIsRejectDialogOpen(false);
  }, [isLoading]);

  const onReject = React.useCallback((data: RejectForm) => {
    reject(
      'reviews',
      { id: review.id, data: { status: 'rejected', comment: data.comment } },
      {
        onSuccess: () => {
          closeRejectDialog();
          onReviewResolved();
          refresh();
        },
        onError: (e) => {
          notify(errorNotification(e, 'Failed to reject review'), { type: 'error' });
          refresh();
        },
      },
    );
  }, [review, reject, closeRejectDialog, onReviewResolved, refresh, notify]);

  return (
    <RecordContextProvider value={review}>
      <Alert
        severity="info"
        icon={<RateReviewIcon />}
      >
        <AlertTitle>Pending Review</AlertTitle>
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body2">
            <strong>Proposed by:</strong>
            {' '}
            <ReferenceField source="proposerId" reference={resourceNames.MEMBERS} link={false}>
              <FunctionField render={(member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`} />
            </ReferenceField>
          </Typography>
          <Typography variant="body2">
            <strong>Submitted:</strong>
            {' '}
            <DateField source="submittedAt" showTime />
          </Typography>
          <Typography variant="body2">
            <strong>Description:</strong>
            {' '}
            <TextField source="actionInformation.description" />
          </Typography>
          <Typography variant="body2">
            <strong>Reasoning:</strong>
            {' '}
            <TextField source="reasoning" />
          </Typography>
          {canApprove && isProposer && (
            <Typography variant="body2" color="warning.dark">
              You cannot approve or reject your own proposed review.
            </Typography>
          )}
        </Box>
        {(canApprove || isProposer) && (
          <Box marginTop={2} display="flex" gap={2}>
            {canApprove && (
              <>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={() => approve()}
                  disabled={!canResolve || isLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => setIsRejectDialogOpen(true)}
                  disabled={!canResolve || isLoading}
                >
                  Reject
                </Button>
              </>
            )}
            {isProposer && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={() => withdraw()}
                disabled={isLoading}
              >
                Withdraw
              </Button>
            )}
          </Box>
        )}
      </Alert>
      <Dialog open={isRejectDialogOpen} onClose={closeRejectDialog} fullWidth maxWidth="sm">
        <Form onSubmit={(data: FieldValues) => onReject(data as RejectForm)} record={{ comment: '' }}>
          <DialogTitle>Reject review</DialogTitle>
          <DialogContent>
            <TextInput
              source="comment"
              label="Rejection comment"
              multiline
              rows={4}
              fullWidth
              validate={[required()]}
              helperText={false}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRejectDialog} color="error" variant="contained" disabled={isLoading}>Close</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
            >
              Reject
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </RecordContextProvider>
  );
};

export default PendingReviewBanner;
